<template>
  <div class="company-list">
    <template v-if="areThereAnyCompanies">
      <div v-if="view === 'grid'">
        <div class="box-selection">
          <company-box
            v-for="company in notArchivedCompanies"
            :key="company.id + company.name"
            :company="company"
            :row-actions="getRowActions(company)"
            @company-clicked="companyClicked"
          />
        </div>
      </div>
      <div
        v-if="view === 'grid' && archivedCompanies.length !== 0"
        class="mt-2"
      >
        <h2>Archived</h2>
        <div class="box-selection mt-4">
          <company-box
            v-for="company in archivedCompanies"
            :key="company.id + company.name"
            :company="company"
            :row-actions="getRowActions(company)"
            @company-clicked="companyClicked"
          />
        </div>
      </div>
      <advanced-table
        v-if="view === 'list'"
        :columns="isSuperAdmin ? [{title: 'Company', name: 'name'}, {title: 'Status', name: 'archived_at'}, ''] : [{title: 'Company', name: 'name'}, {title: 'Status', name: 'archived_at'}]"
        :current-page-new="currentPage"
        :data="sortedCompanies"
        :last-page-new="lastPage"
        :show-load-more-button="false"
        @click="companyClicked($event.selectedRow)"
      >
        <template #default="{row}">
          <td>
            <company-icon :company="row" />
            <span class="company-name">
              {{ row.name }}
            </span>
          </td>
          <td>
            <span>
              {{ row.archived_at !== null ? 'Archived' : '' }}
            </span>
          </td>
          <td
            v-if="getRowActions(row).length > 0"
            class="action-row"
            @click.stop.prevent
          >
            <div
              class="dropdown-holder"
            >
              <context-dropdown
                :min-width="182"
                :options="getRowActions(row)"
                :placement="'bottom-end'"
              />
            </div>
          </td>
        </template>
      </advanced-table>
      <div
        v-if="currentPage < lastPage"
        style="padding-bottom: 20px;"
      >
        <secondary-button @click="changePage(currentPage+1)">
          <span>Load more</span>
        </secondary-button>
      </div>
    </template>
  </div>
</template>
<script>
import { mapActions, mapState } from "vuex"
import { CompanyRoutes } from "../../api/routes"
import Language from "../../i18n/en"
import Utils from "../../utils"
import CompanyIcon from "../common/CompanyIcon"
import ContextDropdown from "../common/dropdown/ContextDropdown"
import CompanyModal from "../common/modal/CompanyModal"
import CompanySettingsModal from "../common/modal/CompanySettingsModal"
import DeleteModal from "../common/modal/DeleteModal"
import EditFeatures from "../common/modal/EditFeatures"
import OfferistaCredentialsModal from "../common/modal/OfferistaCredentialsModal"
import CompanyBox from "./CompanyBox"
import CompanyDetailsModal from "@/components/common/modal/CompanyDetailsModal.vue"
import OpenWeatherCredentialsModal from "@/components/common/modal/OpenWeatherCredentialsModal.vue"
import GoogleMapsCredentialsModal from "@/components/common/modal/GoogleMapsCredentialsModal.vue"

export default {
  components: {
    CompanyIcon,
    CompanyBox,
    ContextDropdown
  },
  props: {
    view: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      currentPage: 1,
      lastPage: 1,
      pageSize: 40,
      currentSort: "created_at",
      currentSortDir: "ASC",
      companies: []
    }
  },
  computed: {
    ...mapState(["currentUser"]),
    areThereAnyCompanies () {
      return this?.companies?.length
    },
    isSuperAdmin () {
      return this.currentUser && this.currentUser.roles.super_admin
    },
    archivedCompanies () {
      return this.companies.filter(c => c.archived_at !== null).sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at)
      })
    },
    notArchivedCompanies () {
      return this.companies.filter(c => c.archived_at === null).sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at)
      })
    },
    sortedCompanies () {
      return [...this.notArchivedCompanies, ...this.archivedCompanies]
    }
  },
  mounted () {
    this.currentPage = 1
    this.fetchData()
  },
  methods: {
    changePage (newPage, limit = 40, append = true) {
      this.currentPage = newPage
      this.fetchData(newPage, limit, this.currentSort, this.currentSortDir, append)
    },
    getRowActions (company) {
      const actions = []
      if (this.isSuperAdmin) {
        actions.push({
          content: "Edit",
          icon: "edit",
          click: () => this.edit(company)
        })
        actions.push({
          content: "Edit details",
          icon: "edit",
          click: () => this.editDetails(company)
        })
        actions.push({
          content: company.archived_at ? "Unarchive" : "Archive",
          icon: "archive",
          click: () => this.archive(company)
        })
        actions.push({
          content: "Delete",
          icon: "delete",
          click: () => this.destroyCompany(company)
        })
        actions.push({
          content: "Offerista",
          icon: "offerista-module-18",
          click: () => this.$modal.show(OfferistaCredentialsModal, {companyUuid: company.uuid})
        })
        actions.push({
          content: "OpenWeather",
          icon: "effect-module-18",
          click: () => this.$modal.show(OpenWeatherCredentialsModal, {companyUuid: company.uuid})
        })
        actions.push({
          content: "GoogleMaps",
          icon: "google-maps-module-18",
          click: () => this.$modal.show(GoogleMapsCredentialsModal, {companyUuid: company.uuid})
        })
        actions.push({
          content: "Company settings",
          icon: "change-company",
          click: () => this.$modal.show(CompanySettingsModal, {companyUuid: company.uuid})
        })
        actions.push({
          content: "Edit features",
          icon: "edit-features-icon",
          click: () => this.editFeatures(company)
        })
      }
      return actions
    },
    edit (company) {
      this.$modal.show(CompanyModal, {
        company,
        title: "Edit Company",
        action: this.fetchData
      })
    },
    editDetails (company) {
      this.$modal.show(CompanyDetailsModal, {
        company,
        title: "Edit Company details",
        action: this.fetchData
      })
    },
    editFeatures (company) {
      this.$modal.show(EditFeatures, {
        company,
        title: "Edit features",
        companyName: company.name,
        companyUuid: company.uuid,
        action: this.fetchData
      })
    },
    archive (company) {
      this.$modal.show(DeleteModal, {
        apiMethod: "put",
        endpoint: company.archived_at ? CompanyRoutes.unarchiveCompany(company.uuid) : CompanyRoutes.archiveCompany(company.uuid),
        title: `${company.archived_at ? "Unarchive" : "Archive"} Company ${company.name}`,
        successMessage: company.archived_at ? Language.company.success.unarchived : Language.company.success.archived,
        errorMessage: company.archived_at ? Language.company.error.unarchived : Language.company.error.archived,
        description: `Are you sure you want to ${company.archived_at ? "un" : ""}archive?`,
        actionButton: company.archived_at ? "Unarchive" : "Archive",
        afterDelete: this.fetchData
      })
    },
    destroyCompany (company) {
      this.$modal.show(DeleteModal, {
        endpoint: CompanyRoutes.deleteCompany(company.uuid),
        title: "Delete campaign",
        description: "Are you sure to delete this company?",
        actionButton: Language.confirm_delete,
        successMessage: Language.company.success.delete,
        errorMessage: Language.company.error.delete,
        afterDelete: () => {
          this.companies = this.companies.filter(c => c.uuid !== company.uuid)
        }
      })
    },
    fetchData (page = 1, limit = this.pageSize, sortBy = this.currentSort, sortWay = this.currentSortDir, append = false) {
      const loadCompanies = this.loadCompanies({limit, page, sortBy, sortWay, append, performCommit: false})

      if (loadCompanies) {
        loadCompanies.then((resp) => {
          this.currentPage = resp.data.meta.current_page
          this.lastPage = resp.data.meta.last_page
          this.companies = Utils.removeDuplicatesByKey([...resp.data.data, ...this.companies], "uuid")

          const params = new URLSearchParams(window.location.search)
          const redirect = parseInt(params.get("autoRedirect")) || 0

          if (redirect === 1 && resp.data.meta.total <= 1 && !this.isSuperAdmin) {
            this.$router.push("/dashboard")
          }
        })
      }

      return loadCompanies
    },
    companyClicked (company) {
      if (company.archived_at !== null) {
        this.$toasted.error("Cannot enter archived company, please unarchive it if you want to access it")
        return
      }

      return this.changeCompany(company.id).then(() => {
        this.$toasted.success(`Switched to ${company.name} company`)

        const params = new URLSearchParams(window.location.search)
        const redirect = params.get("redirectAfter") || "/"
        this.$router.push(redirect)
      })
    },
    ...mapActions(["loadCompanies", "changeCompany"])
  }
}
</script>
